import { useEffect, useMemo, useState } from "react"
import { Shell } from "@/components/Shell"
import { listWeight, upsertWeight, type WeightEntry } from "@/lib/api"
import { localToday } from "@/lib/dates"
import { useAuth } from "@/lib/auth"
import {
  useWeightUnit,
  toDisplay,
  fromDisplay,
  formatDisplay,
  formatDelta,
} from "@/lib/units"

/**
 * Weight page — line chart with raw points + 7-day rolling average overlay.
 * Quick-entry at the top. SVG-rendered chart (no library dep).
 */
export function WeightPage() {
  const auth = useAuth()
  const { unit, setUnit } = useWeightUnit()
  const [entries, setEntries] = useState<WeightEntry[]>([])
  // `today` is the value as entered, in the user's CURRENT display unit.
  const [today, setToday] = useState<number | "">("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const load = async () => {
    try {
      setEntries(await listWeight(365))
    } catch (e: any) {
      setError(e?.message ?? "Load failed")
    }
  }
  useEffect(() => { void load() }, [])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (typeof today !== "number" || today <= 0) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      // Convert display unit → kg at the boundary; storage is always kg.
      // Tag by user's local day, not server UTC (M11): otherwise a weight
      // logged at 9pm PDT lands on tomorrow's UTC date.
      await upsertWeight({
        date: localToday(),
        weight_kg: fromDisplay(today, unit),
      })
      if (auth.status === "authed") await auth.refresh()
      await load()
      setToday("")
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Save failed")
    } finally {
      setBusy(false)
    }
  }

  const latest = entries[0]
  const stats = useMemo(() => computeStats(entries), [entries])

  return (
    <Shell
      currentPath="/weight"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-3">weight · log + trend</p>
        <h1 className="font-display text-display tracking-tightest mb-10">
          The <span className="italic text-moss">number</span> on the scale.
        </h1>

        <div className="grid lg:grid-cols-[1fr_auto] gap-12">
          <form onSubmit={onSubmit} className="card-surface px-6 sm:px-8 py-6 space-y-4 self-start">
            <div className="flex items-baseline justify-between">
              <p className="eyebrow">today's weight</p>
              <UnitToggle value={unit} onChange={setUnit} />
            </div>
            <div className="flex items-baseline gap-3 border-b border-border focus-within:border-moss py-2">
              <input
                type="number"
                step={0.1}
                min={0}
                value={today}
                onChange={(e) => setToday(e.target.value === "" ? "" : parseFloat(e.target.value))}
                placeholder="—"
                className="flex-1 min-w-0 bg-transparent border-0 outline-none font-display text-4xl tracking-tight num"
              />
              <span className="font-mono text-sm text-muted-foreground">{unit}</span>
            </div>
            <button
              type="submit"
              disabled={busy || !today}
              className="bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Log weight"}
            </button>
            {saved && <p className="eyebrow text-moss">saved · targets updated</p>}
            {error && <p className="font-sans text-sm text-persimmon">{error}</p>}
          </form>

          {latest && (
            <div className="card-surface px-6 sm:px-8 py-6 self-start min-w-[18rem]">
              <p className="eyebrow mb-2">latest entry</p>
              <p className="font-display text-5xl num tracking-tightest">
                {toDisplay(latest.weight_kg, unit).toFixed(1)}
                <span className="font-mono text-sm text-muted-foreground ml-1">{unit}</span>
              </p>
              <p className="font-mono text-xs text-muted-foreground mt-1">{latest.date}</p>
              {stats.avg7 != null && (
                <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="eyebrow mb-1">7d avg</p>
                    <p className="font-mono">{formatDisplay(stats.avg7, unit)}</p>
                  </div>
                  <div>
                    <p className="eyebrow mb-1">vs 7d ago</p>
                    <p className={`font-mono ${stats.delta7 != null && stats.delta7 < 0 ? "text-moss" : "text-muted-foreground"}`}>
                      {stats.delta7 != null ? formatDelta(stats.delta7, unit) : "—"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <section className="mt-14">
          <p className="eyebrow mb-4">trend · {entries.length} entries · {unit}</p>
          {entries.length < 2 ? (
            <p className="font-sans text-sm text-muted-foreground">
              Log at least two weights to see the trend.
            </p>
          ) : (
            <WeightChart entries={entries} unit={unit} />
          )}
        </section>
      </div>
    </Shell>
  )
}

function UnitToggle({
  value,
  onChange,
}: {
  value: "kg" | "lb"
  onChange: (u: "kg" | "lb") => void
}) {
  return (
    <div
      className="inline-flex border border-border rounded overflow-hidden"
      role="radiogroup"
      aria-label="Weight unit"
    >
      {(["kg", "lb"] as const).map((u) => {
        const active = u === value
        return (
          <button
            key={u}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(u)}
            className={`px-3 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors ${
              active
                ? "bg-foreground text-background"
                : "bg-surface text-muted-foreground hover:text-foreground"
            }`}
          >
            {u}
          </button>
        )
      })}
    </div>
  )
}

function computeStats(entries: WeightEntry[]) {
  if (entries.length === 0) return { avg7: null as number | null, delta7: null as number | null }
  // entries are date DESC
  const recent7 = entries.slice(0, 7)
  const avg7 = recent7.reduce((a, e) => a + e.weight_kg, 0) / recent7.length
  const idx7 = entries.findIndex((_, i) => i >= 6) // ~7 entries ago
  const delta7 = idx7 >= 0 ? entries[0].weight_kg - entries[idx7].weight_kg : null
  return { avg7, delta7 }
}

function WeightChart({ entries, unit }: { entries: WeightEntry[]; unit: "kg" | "lb" }) {
  // Sort ascending for charting. Convert each point to display unit so the
  // y-axis labels match the user's chosen scale.
  const data = [...entries]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((e) => ({ date: e.date, w: toDisplay(e.weight_kg, unit) }))
  const W = 720, H = 220, PAD = 28
  const ws = data.map((d) => d.w)
  const min = Math.min(...ws), max = Math.max(...ws)
  const range = max - min || 1
  const xs = (i: number) => PAD + (i * (W - 2 * PAD)) / Math.max(1, data.length - 1)
  const ys = (w: number) => H - PAD - ((w - min) / range) * (H - 2 * PAD)
  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xs(i)} ${ys(d.w)}`).join(" ")
  // 7-day rolling avg
  const roll = data.map((_, i) => {
    const start = Math.max(0, i - 6)
    const slice = data.slice(start, i + 1)
    return slice.reduce((acc, x) => acc + x.w, 0) / slice.length
  })
  const rollPath = roll.map((w, i) => `${i === 0 ? "M" : "L"} ${xs(i)} ${ys(w)}`).join(" ")

  return (
    <div className="card-surface px-2 sm:px-4 py-4 overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        {/* Y baseline */}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="hsl(var(--border))" strokeWidth={1} />
        {/* 7d avg (thick translucent moss) */}
        <path d={rollPath} fill="none" stroke="hsl(var(--moss))" strokeWidth={6} strokeLinecap="round" opacity={0.18} />
        {/* Raw line (thin ink) */}
        <path d={linePath} fill="none" stroke="hsl(var(--foreground))" strokeWidth={1.25} />
        {/* Points */}
        {data.map((d, i) => (
          <circle key={d.date} cx={xs(i)} cy={ys(d.w)} r={2.5} fill="hsl(var(--foreground))" />
        ))}
        {/* Y labels */}
        <text x={4} y={ys(max)} fontSize={10} fill="hsl(var(--muted-foreground))" dominantBaseline="middle">{max.toFixed(1)}</text>
        <text x={4} y={ys(min)} fontSize={10} fill="hsl(var(--muted-foreground))" dominantBaseline="middle">{min.toFixed(1)}</text>
        {/* X endpoint labels */}
        <text x={PAD} y={H - 6} fontSize={10} fill="hsl(var(--muted-foreground))" textAnchor="start">{data[0].date}</text>
        <text x={W - PAD} y={H - 6} fontSize={10} fill="hsl(var(--muted-foreground))" textAnchor="end">{data[data.length - 1].date}</text>
      </svg>
      <div className="px-2 pt-2 flex items-center gap-6 text-xs">
        <span className="flex items-center gap-2">
          <span className="block w-3 h-px bg-foreground" /> daily
        </span>
        <span className="flex items-center gap-2">
          <span className="block w-3 h-[6px] bg-moss/30" /> 7-day rolling average
        </span>
      </div>
    </div>
  )
}
